/**
 * DogDetailCard — Hundekarte mit Stammdaten und den beiden Pflichtnachweisen.
 *
 * Extrahiert aus `pages/DogManagementBeta.tsx`, damit Hundeverwaltung und
 * `DogDetailModal` (u. a. vom Vorstands-Dashboard aus) dieselbe Darstellung und
 * dieselben Aktionen nutzen. Rein präsentational — alle Aktionen kommen als
 * Callbacks herein, Daten und Fehlerbehandlung bleiben beim Aufrufer.
 *
 * Icons folgen der Matrix aus Issue #9 (Heroicons Outline, `SyringeIcon` für
 * Tollwut, `ShieldCheckIcon` für Haftpflicht, `SideDogIcon` für den Hund).
 */
import React from 'react';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  PencilIcon,
  ShieldCheckIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import AttributeLabel from './AttributeLabel';
import GuestBadge from './GuestBadge';
import SideDogIcon from './icons/SideDogIcon';
import SyringeIcon from './icons/SyringeIcon';
import { getDogImageUrl } from '../utils/imageUtils';
import {
  dogAgeLabel,
  dogFlagLabel,
  dogHasIssues,
  isTollwutExpired,
  type Dog,
  type DogDocumentCategory,
} from '../types/dog';

export interface DogDetailCardProps {
  dog: Dog;
  /** Halter-Name; wird als zusätzliche Stammdatenzeile gezeigt, wenn gesetzt. */
  owner?: string;
  /** Halter ist reiner Gast — Badge hinter dem Namen. */
  ownerIsGuest?: boolean;
  onEdit?: () => void;
  /** Klick auf das Foto (Bildbetrachter). Ohne Callback ist das Bild nicht klickbar. */
  onOpenImage?: () => void;
  onPreviewDocuments: (category: DogDocumentCategory) => void;
  onToggleInsurance: () => void;
  onToggleTollwut: () => void;
  /** Bild konnte nicht geladen werden → Platzhalter zeigen. */
  imageError?: boolean;
  onImageError?: () => void;
  /** Karten-Rahmen weglassen (z. B. innerhalb eines Modals). */
  bare?: boolean;
  className?: string;
}

const TILE_OK = 'bg-success-100 text-success-600 dark:bg-success-900/30 dark:text-success-400';
const TILE_PENDING = 'bg-warning-100 text-warning-600 dark:bg-warning-900/30 dark:text-warning-400';
const TILE_BAD = 'bg-error-100 text-error-600 dark:bg-error-900/30 dark:text-error-400';

const BADGE_OK =
  'inline-flex items-center gap-1 rounded-md bg-success-50 px-1.5 py-0.5 text-[11px] font-semibold text-success-700 dark:bg-success-900/30 dark:text-success-300';
const BADGE_PENDING =
  'inline-flex items-center gap-1 rounded-md bg-warning-50 px-1.5 py-0.5 text-[11px] font-semibold text-warning-700 dark:bg-warning-900/30 dark:text-warning-300';
const BADGE_BAD =
  'inline-flex items-center gap-1 rounded-md bg-error-50 px-1.5 py-0.5 text-[11px] font-semibold text-error-700 dark:bg-error-900/30 dark:text-error-300';

const BTN_NEUTRAL =
  'flex items-center gap-1.5 rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700';
const BTN_REVOKE =
  'flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors border-warning-200 bg-warning-50 text-warning-700 hover:bg-warning-100 dark:border-warning-800/60 dark:bg-warning-900/30 dark:text-warning-300';
const BTN_APPROVE =
  'flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors border-success-200 bg-success-50 text-success-700 hover:bg-success-100 dark:border-success-800/60 dark:bg-success-900/30 dark:text-success-300';

const de = (v?: string | null) => (v ? new Date(v).toLocaleDateString('de-DE') : '');

const DogDetailCard: React.FC<DogDetailCardProps> = ({
  dog,
  owner,
  ownerIsGuest = false,
  onEdit,
  onOpenImage,
  onPreviewDocuments,
  onToggleInsurance,
  onToggleTollwut,
  imageError = false,
  onImageError,
  bare = false,
  className = '',
}) => {
  const hasIssues = dogHasIssues(dog);
  const insuranceValid = dog.valid_insurance && dog.documents.insurance.length > 0;
  const insurancePending = !dog.valid_insurance && dog.documents.insurance.length > 0;
  const tollwutExpired = isTollwutExpired(dog);
  const tollwutValid = dog.valid_tollwut && dog.documents.tollwut.length > 0 && !tollwutExpired;
  const tollwutPending = !dog.valid_tollwut && dog.documents.tollwut.length > 0 && !tollwutExpired;

  const showImage = !!dog.image_path && !imageError;
  const imageClickable = showImage && !!onOpenImage;

  const frame = bare
    ? 'relative overflow-hidden'
    : `relative overflow-hidden rounded-2xl border bg-white shadow-sm transition-all dark:bg-[#1c2432] ${
        hasIssues ? 'border-error-200 dark:border-error-800/60' : 'border-gray-200 dark:border-gray-700'
      }`;

  return (
    <div className={`${frame} ${className}`.trim()}>
      {/* Fehler-Indikator: dezenter linker Streifen statt komplett roter Fläche */}
      {hasIssues && !bare && (
        <span className="absolute inset-y-0 left-0 w-1 bg-error-500" aria-hidden="true" />
      )}

      {/* Kopf: Foto neben Stammdaten */}
      <div className="flex gap-4 p-4">
        <div
          className={`group relative h-24 w-24 flex-shrink-0 overflow-hidden rounded-xl bg-primary-600 ${
            imageClickable ? 'cursor-pointer' : ''
          }`}
          onClick={() => imageClickable && onOpenImage?.()}
        >
          {showImage ? (
            <img
              src={getDogImageUrl(dog.dog_id, 'M', dog.image_path_updated || undefined)}
              alt={dog.hundename}
              className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
              onError={onImageError}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <SideDogIcon className="h-10 w-10 text-white/60" />
            </div>
          )}
          {imageClickable && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-all duration-200 group-hover:bg-black/25 group-hover:opacity-100">
              <EyeIcon className="h-6 w-6 text-white" />
            </div>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <h3 className="truncate text-lg font-bold text-gray-900 dark:text-white">
              {dog.hundename}
            </h3>
            <div className="flex flex-shrink-0 items-center gap-2">
              {hasIssues ? (
                <span className="inline-flex items-center gap-1 rounded-md border border-error-200 bg-error-50 px-2 py-0.5 text-xs font-semibold text-error-700 dark:border-error-800/60 dark:bg-error-900/30 dark:text-error-300">
                  <ExclamationTriangleIcon className="h-3.5 w-3.5" aria-hidden="true" />
                  Probleme
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 rounded-md border border-success-200 bg-success-50 px-2 py-0.5 text-xs font-semibold text-success-700 dark:border-success-800/60 dark:bg-success-900/30 dark:text-success-300">
                  <CheckCircleIcon className="h-3.5 w-3.5" aria-hidden="true" />
                  Alles gültig
                </span>
              )}
              {onEdit && (
                <button
                  type="button"
                  onClick={onEdit}
                  className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 text-gray-500 transition-colors hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700"
                  title="Hund bearbeiten"
                >
                  <PencilIcon className="h-4 w-4" aria-hidden="true" />
                </button>
              )}
            </div>
          </div>

          <div className="mt-2 grid grid-cols-1 gap-x-4 gap-y-1.5 text-sm sm:grid-cols-2">
            {owner && (
              <div className="flex items-center gap-1.5 sm:col-span-2">
                <AttributeLabel iconKey="owner" size="sm" textClassName="text-gray-500 dark:text-gray-400">
                  Halter:
                </AttributeLabel>
                <span className="truncate font-medium text-gray-900 dark:text-gray-100">{owner}</span>
                {ownerIsGuest && <GuestBadge />}
              </div>
            )}
            <div className="flex items-center gap-1.5">
              <AttributeLabel iconKey="breed" size="sm" textClassName="text-gray-500 dark:text-gray-400">
                Rasse:
              </AttributeLabel>
              <span className="truncate font-medium text-gray-900 dark:text-gray-100">
                {dog.rasse || 'Unbekannt'}
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <AttributeLabel iconKey="age" size="sm" textClassName="text-gray-500 dark:text-gray-400">
                Alter:
              </AttributeLabel>
              <span className="truncate font-medium text-gray-900 dark:text-gray-100">
                {dogAgeLabel(dog.geburtstag)}
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <AttributeLabel iconKey="neutered" size="sm" textClassName="text-gray-500 dark:text-gray-400">
                Kastriert:
              </AttributeLabel>
              <span className="font-medium text-gray-900 dark:text-gray-100">
                {dogFlagLabel(dog.kastriert)}
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <AttributeLabel iconKey="dogBox" size="sm" textClassName="text-gray-500 dark:text-gray-400">
                Hundebox:
              </AttributeLabel>
              <span className="font-medium text-gray-900 dark:text-gray-100">
                {dogFlagLabel(dog.hundebox)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Nachweise als kompakte Prüf-Zeilen */}
      <div className="border-t border-gray-200 dark:border-gray-700">
        {/* Haftpflichtversicherung */}
        <div className="flex flex-wrap items-center gap-3 border-b border-gray-200 p-4 dark:border-gray-700">
          <div
            className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl ${
              insuranceValid ? TILE_OK : insurancePending ? TILE_PENDING : TILE_BAD
            }`}
          >
            <ShieldCheckIcon className="h-5 w-5" aria-hidden="true" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Haftpflichtversicherung
              </span>
              {insuranceValid ? (
                <span className={BADGE_OK}>
                  <CheckCircleIcon className="h-3 w-3" aria-hidden="true" />
                  Genehmigt
                </span>
              ) : insurancePending ? (
                <span className={BADGE_PENDING}>
                  <ExclamationTriangleIcon className="h-3 w-3" aria-hidden="true" />
                  Prüfung ausstehend
                </span>
              ) : (
                <span className={BADGE_BAD}>
                  <XCircleIcon className="h-3 w-3" aria-hidden="true" />
                  Dokumente fehlen
                </span>
              )}
            </div>
            <div className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">
              {dog.documents.insurance.length}{' '}
              {dog.documents.insurance.length === 1 ? 'Dokument' : 'Dokumente'}
              {dog.insurance_name && dog.insurance_name !== 'Unbekannt' && <> · {dog.insurance_name}</>}
              {dog.insurance_checked_by && dog.insurance_checked_at && (
                <>
                  {' '}
                  · geprüft von {dog.insurance_checked_by} am {de(dog.insurance_checked_at)}
                </>
              )}
            </div>
          </div>
          <div className="flex flex-shrink-0 gap-2">
            <button
              type="button"
              onClick={() => onPreviewDocuments('insurance')}
              className={BTN_NEUTRAL}
              title="Dokumente anzeigen"
            >
              <EyeIcon className="h-4 w-4" aria-hidden="true" />
              <span className="hidden sm:inline">Dokumente</span>
            </button>
            <button
              type="button"
              onClick={onToggleInsurance}
              className={dog.valid_insurance ? BTN_REVOKE : BTN_APPROVE}
              title={dog.valid_insurance ? 'Genehmigung entziehen' : 'Dokumente genehmigen'}
            >
              {dog.valid_insurance ? (
                <>
                  <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                  Entziehen
                </>
              ) : (
                <>
                  <CheckCircleIcon className="h-4 w-4" aria-hidden="true" />
                  Genehmigen
                </>
              )}
            </button>
          </div>
        </div>

        {/* Tollwutimpfung */}
        <div className="flex flex-wrap items-center gap-3 p-4">
          <div
            className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl ${
              tollwutValid ? TILE_OK : tollwutPending || tollwutExpired ? TILE_PENDING : TILE_BAD
            }`}
          >
            <SyringeIcon className="h-5 w-5" aria-hidden="true" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Tollwutimpfung
              </span>
              {tollwutValid ? (
                <span className={BADGE_OK}>
                  <CheckCircleIcon className="h-3 w-3" aria-hidden="true" />
                  Genehmigt
                </span>
              ) : tollwutExpired ? (
                <span className={BADGE_BAD}>
                  <ExclamationTriangleIcon className="h-3 w-3" aria-hidden="true" />
                  Abgelaufen
                </span>
              ) : tollwutPending ? (
                <span className={BADGE_PENDING}>
                  <ExclamationTriangleIcon className="h-3 w-3" aria-hidden="true" />
                  Prüfung ausstehend
                </span>
              ) : (
                <span className={BADGE_BAD}>
                  <XCircleIcon className="h-3 w-3" aria-hidden="true" />
                  Dokumente fehlen
                </span>
              )}
            </div>
            <div className="mt-0.5 truncate text-xs text-gray-500 dark:text-gray-400">
              {dog.documents.tollwut.length}{' '}
              {dog.documents.tollwut.length === 1 ? 'Dokument' : 'Dokumente'}
              {dog.tollwut_end_date && (
                <>
                  {' '}
                  · {tollwutExpired ? 'abgelaufen am' : 'gültig bis'} {de(dog.tollwut_end_date)}
                </>
              )}
              {dog.tollwut_checked_by && dog.tollwut_checked_at && (
                <>
                  {' '}
                  · geprüft von {dog.tollwut_checked_by} am {de(dog.tollwut_checked_at)}
                </>
              )}
            </div>
          </div>
          <div className="flex flex-shrink-0 gap-2">
            <button
              type="button"
              onClick={() => onPreviewDocuments('tollwut')}
              className={BTN_NEUTRAL}
              title="Impfpass anzeigen"
            >
              <EyeIcon className="h-4 w-4" aria-hidden="true" />
              <span className="hidden sm:inline">Impfpass</span>
            </button>
            <button
              type="button"
              onClick={onToggleTollwut}
              className={dog.valid_tollwut ? BTN_REVOKE : BTN_APPROVE}
              title={dog.valid_tollwut ? 'Genehmigung entziehen' : 'Impfung genehmigen'}
            >
              {dog.valid_tollwut ? (
                <>
                  <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                  Entziehen
                </>
              ) : (
                <>
                  <CheckCircleIcon className="h-4 w-4" aria-hidden="true" />
                  Genehmigen
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DogDetailCard;
